import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import * as TwilioVideo from 'twilio-video';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  videoConferenceService,
  VideoCallRequest,
  ClinicalDataResponse,
  VideoCallChatMessage,
} from '../../services/video-conference/videoConferenceService';
import { dashboardService, AppointmentWithDetails } from '../../services/dashboard/dashboardService';
import { getChatStatusWebSocketUrl } from '../../../../shared/utils/constants';
import './VideoConferencePage.css';

type ChatSubTab = 'summary' | 'prescription' | 'labTests';

// Utility function to format markdown text for display
const formatChatMessage = (content: string): string => {
  if (!content) return '';
  
  // Remove "Appointment Summary Generated" prefix
  let formatted = content
    .replace(/Appointment Summary Generated\n\n/g, '')
    .replace(/Appointment Summary Generated/g, '');
  
  // Escape HTML first to prevent XSS
  formatted = formatted
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Convert markdown formatting to HTML
  // Convert **bold** to <strong>bold</strong> (non-greedy match)
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert line breaks to <br>
  formatted = formatted.replace(/\n/g, '<br>');
  
  return formatted;
};

const VideoConferencePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [appointment, setAppointment] = useState<AppointmentWithDetails | null>(null);
  const [callRequest, setCallRequest] = useState<VideoCallRequest | null>(null);
  const [clinicalData, setClinicalData] = useState<ClinicalDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Video call state
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [callDuration, setCallDuration] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [isCallEnded, setIsCallEnded] = useState(false);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  
  // Media permissions state
  const [permissionError, setPermissionError] = useState<string | null>(null);
  
  // Twilio Video state
  const [twilioRoom, setTwilioRoom] = useState<TwilioVideo.Room | null>(null);
  
  // Keep refs in sync with state for use in callbacks
  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);
  
  useEffect(() => {
    twilioRoomRef.current = twilioRoom;
  }, [twilioRoom]);

  useEffect(() => {
    callRequestRef.current = callRequest;
  }, [callRequest]);
  const [localVideoTrack, setLocalVideoTrack] = useState<TwilioVideo.LocalVideoTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<TwilioVideo.LocalAudioTrack | null>(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState<TwilioVideo.RemoteVideoTrack | null>(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState<TwilioVideo.RemoteTrack | null>(null);
  const [hasRemoteParticipant, setHasRemoteParticipant] = useState(false);
  
  // Discussion panel state
  const [activeChatSubTab, setActiveChatSubTab] = useState<ChatSubTab>('summary');
  const [chatMessages, setChatMessages] = useState<VideoCallChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [chatSessionId, setChatSessionId] = useState<number | null>(null);
  const [selectedRecipient, setSelectedRecipient] = useState<'patient' | 'ai_agent'>('patient');
  const [isAgentProcessing, setIsAgentProcessing] = useState(false);
  const [editingPrescriptionId, setEditingPrescriptionId] = useState<string | null>(null);
  const [editingLabTestId, setEditingLabTestId] = useState<number | null>(null);
  const [isAddingPrescription, setIsAddingPrescription] = useState(false);
  const [isAddingLabTest, setIsAddingLabTest] = useState(false);
  const [editPrescriptionForm, setEditPrescriptionForm] = useState({
    medication_name: '',
    dosage: '',
    frequency: '',
    instructions: '',
    duration: '',
  });
  const [editLabTestForm, setEditLabTestForm] = useState({
    test_name: '',
    test_category: '',
    reason: '',
    priority: '',
    instructions: '',
    notes: '',
  });
  
  const callStartTimeRef = useRef<Date | null>(null);
  const chatMessagesEndRef = useRef<HTMLDivElement | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const reloadDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasConnectedRef = useRef<boolean>(false);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionStartTimeRef = useRef<number | null>(null);
  const twilioRoomRef = useRef<TwilioVideo.Room | null>(null);
  const connectionStatusRef = useRef<'connecting' | 'connected' | 'disconnected'>('connecting');
  const websocketRef = useRef<WebSocket | null>(null);
  const websocketReconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callRequestRef = useRef<VideoCallRequest | null>(null);
  const appointmentCompletedRef = useRef<boolean>(false);
  
  // Callback ref for local video to attach track immediately when element is ready
  const localVideoCallbackRef = useCallback((element: HTMLVideoElement | null) => {
    console.log('🎥 Local video element callback:', {
      elementExists: !!element,
      hasTrack: !!localVideoTrack,
      elementVisible: element ? (element.offsetWidth > 0 && element.offsetHeight > 0) : false
    });
    
    localVideoRef.current = element;
    if (element && localVideoTrack) {
      console.log('🔗 Attaching local video track to element via callback ref');
      try {
        // Detach from any previous element first
        if (element.srcObject) {
          console.log('Detaching from previous element');
          localVideoTrack.detach(element);
        }
        // Attach the track to the element
        localVideoTrack.attach(element);
        console.log('✅ Track attached via callback ref, enabling:', isCameraEnabled);
        
        // Verify attachment
        setTimeout(() => {
          if (element.srcObject) {
            console.log('✅ Video element has srcObject via callback ref');
            console.log('Video element details:', {
              videoWidth: element.videoWidth,
              videoHeight: element.videoHeight,
              readyState: element.readyState
            });
          } else {
            console.error('❌ Video element does NOT have srcObject via callback ref!');
          }
        }, 200);
        
        // Enable the track
        if (localVideoTrack.enable) {
          localVideoTrack.enable(isCameraEnabled);
        }
        console.log('✅ Local video track attached successfully via callback ref');
      } catch (err) {
        console.error('❌ Error attaching local video track:', err);
      }
    } else if (element) {
      console.log('⏳ Local video element ready but no track yet');
    }
  }, [localVideoTrack, isCameraEnabled]);


  // Connect to Twilio room
  const connectToTwilioRoom = useCallback(async (roomName: string, doctorId: number) => {
    // Prevent multiple simultaneous connection attempts
    // But allow retry if stuck in connecting state for too long
    // Use ref to avoid dependency on connectionStatus state
    if (hasConnectedRef.current && connectionStatusRef.current === 'connected') {
      console.log('⚠️ Already connected to Twilio room, skipping...');
      return;
    }
    
    // If stuck in connecting state, allow retry after a delay
    if (connectionStatusRef.current === 'connecting' && hasConnectedRef.current) {
      console.log('⚠️ Connection already in progress, but allowing retry...');
      // Reset the flag to allow retry
      hasConnectedRef.current = false;
    }

    console.log('🔄 Starting connection to Twilio room:', { roomName, doctorId });
    setConnectionStatus('connecting');
    setPermissionError(null);
    
    // Clear any existing timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    // Record connection start time
    connectionStartTimeRef.current = Date.now();
    
    // Set a timeout to detect stuck connections (30 seconds)
    connectionTimeoutRef.current = setTimeout(() => {
      // Check if we're still connecting and haven't connected
      // Use refs to get current values, not stale closure values
      if (hasConnectedRef.current && !twilioRoomRef.current && connectionStartTimeRef.current) {
        const elapsed = Date.now() - connectionStartTimeRef.current;
        if (elapsed >= 30000) {
          console.error('⏱️ Connection timeout - connection took too long');
          hasConnectedRef.current = false;
          setConnectionStatus('disconnected');
          setPermissionError('Connection timeout. Please check your camera/microphone permissions and try again.');
          connectionStartTimeRef.current = null;
        }
      }
    }, 30000);
    
    // Mark as attempting connection to prevent race conditions
    hasConnectedRef.current = true;

    let videoTrack: TwilioVideo.LocalVideoTrack | null = null;
    let audioTrack: TwilioVideo.LocalAudioTrack | null = null;

    try {
      // Create local tracks first and get them directly
      try {
        console.log('📹 Requesting camera and microphone permissions...');
        console.log('📹 Checking media device availability...');
        
        // Check if media devices are available
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter(d => d.kind === 'videoinput');
          const audioDevices = devices.filter(d => d.kind === 'audioinput');
          console.log('📹 Available devices:', {
            videoDevices: videoDevices.length,
            audioDevices: audioDevices.length,
            videoDeviceNames: videoDevices.map(d => d.label || 'Unknown'),
            audioDeviceNames: audioDevices.map(d => d.label || 'Unknown')
          });
        } catch (enumErr) {
          console.warn('⚠️ Could not enumerate devices:', enumErr);
        }
        
        // Create local video track
        console.log('📹 Creating local video track...');
        videoTrack = await TwilioVideo.createLocalVideoTrack({
          width: 1280,
          height: 720,
          frameRate: 24,
        });
        
        console.log('✅ Local video track created successfully');
        console.log('📹 Video track details:', {
          hasTrack: !!videoTrack,
          trackKind: videoTrack ? 'video' : 'none'
        });
        
        // Create local audio track
        console.log('🎤 Creating local audio track...');
        audioTrack = await TwilioVideo.createLocalAudioTrack();
        console.log('✅ Local audio track created successfully');
        
        // Set in state
        setLocalVideoTrack(videoTrack);
        setLocalAudioTrack(audioTrack);
        
        // Enable camera and microphone by default
        setIsCameraEnabled(true);
        setIsMuted(false);
        setPermissionError(null);
        
        console.log('📹 Local video track created successfully, attaching to element...');
        
        // Try to attach immediately if ref is already available
        setTimeout(() => {
          if (localVideoRef.current && videoTrack) {
            console.log('🔗 Attaching local video track immediately after creation');
            try {
              videoTrack.attach(localVideoRef.current);
              if (videoTrack.enable) {
                videoTrack.enable(true);
              }
              console.log('✅ Local video track attached successfully');
            } catch (err) {
              console.error('❌ Error attaching track immediately:', err);
            }
          } else {
            console.warn('⚠️ Local video ref not available yet:', {
              hasRef: !!localVideoRef.current,
              hasTrack: !!videoTrack
            });
          }
        }, 100);
      } catch (err: any) {
        console.error('❌ Failed to create local tracks:', err);
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          code: err.code,
          stack: err.stack,
          fullError: err
        });
        let errorMessage = 'Failed to access camera and microphone.';
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = 'Camera and microphone access was denied. Please allow access in your browser settings and try again.';
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = 'No camera or microphone found. Please connect a device and try again.';
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage = 'Camera or microphone is already in use by another application. Please close other applications using the camera.';
        } else if (err.message) {
          errorMessage = `Failed to access camera/microphone: ${err.message}`;
        }
        // Clear connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setPermissionError(errorMessage);
        setIsCameraEnabled(false);
        setIsMuted(true);
        setConnectionStatus('disconnected');
        hasConnectedRef.current = false;
        return;
      }

      if (!videoTrack || !audioTrack) {
        console.error('Failed to create local tracks');
        hasConnectedRef.current = false;
        setConnectionStatus('disconnected');
        setPermissionError('Failed to create video or audio tracks. Please try again.');
        return;
      }

      // Get Twilio token
      console.log('🔑 Requesting Twilio token...');
      let tokenResponse;
      try {
        tokenResponse = await videoConferenceService.createTwilioToken(
        roomName,
        `doctor-${doctorId}`
      );
        console.log('✅ Twilio token received:', {
          roomName: tokenResponse.room_name,
          tokenLength: tokenResponse.token.length,
          identity: tokenResponse.identity
        });
      } catch (tokenErr: any) {
        console.error('❌ Failed to get Twilio token:', tokenErr);
        console.error('Token error details:', {
          message: tokenErr.message,
          response: tokenErr.response?.data,
          status: tokenErr.response?.status
        });
        // Clear connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        hasConnectedRef.current = false;
        setConnectionStatus('disconnected');
        const tokenErrorMsg = tokenErr.response?.data?.detail || tokenErr.message || 'Failed to get video call token. Please try again.';
        setPermissionError(tokenErrorMsg);
        // Clean up tracks
        if (videoTrack) {
          try {
            videoTrack.stop();
            videoTrack.detach();
          } catch (e) {
            console.error('Error cleaning up video track:', e);
          }
        }
        if (audioTrack) {
          try {
            audioTrack.stop();
          } catch (e) {
            console.error('Error cleaning up audio track:', e);
          }
        }
        setLocalVideoTrack(null);
        setLocalAudioTrack(null);
        return;
      }

      // Connect to room
      console.log('🌐 Connecting to Twilio room:', {
        roomName: tokenResponse.room_name,
        hasVideoTrack: !!videoTrack,
        hasAudioTrack: !!audioTrack,
        tokenLength: tokenResponse.token.length
      });
      
      let room: TwilioVideo.Room | null = null;
      try {
        room = await TwilioVideo.connect(tokenResponse.token, {
        name: tokenResponse.room_name,
        tracks: [videoTrack, audioTrack],
      });
        console.log('✅ Twilio connect() promise resolved');
      } catch (connectErr: any) {
        console.error('❌ Failed to connect to Twilio room:', connectErr);
        console.error('Connection error details:', {
          name: connectErr.name,
          message: connectErr.message,
          code: connectErr.code,
          twilioError: connectErr
        });
        // Clear connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        hasConnectedRef.current = false;
        setConnectionStatus('disconnected');
        let connectErrorMsg = 'Failed to connect to video room. ';
        if (connectErr.message?.includes('token') || connectErr.code === 20101) {
          connectErrorMsg += 'Invalid or expired token. Please refresh the page.';
        } else if (connectErr.message?.includes('room')) {
          connectErrorMsg += 'Room not found or unavailable.';
        } else {
          connectErrorMsg += connectErr.message || 'Please try again.';
        }
        setPermissionError(connectErrorMsg);
        // Clean up tracks
        if (videoTrack) {
          try {
            videoTrack.stop();
            videoTrack.detach();
          } catch (e) {
            console.error('Error cleaning up video track:', e);
          }
        }
        if (audioTrack) {
          try {
            audioTrack.stop();
          } catch (e) {
            console.error('Error cleaning up audio track:', e);
          }
        }
        setLocalVideoTrack(null);
        setLocalAudioTrack(null);
        return;
      }

      console.log('✅ Twilio connect() promise resolved, room object received');
      console.log('Room details:', {
        roomName: tokenResponse.room_name,
        hasRoom: !!room
      });
      
      // Verify connection was successful before proceeding
      if (!room) {
        hasConnectedRef.current = false;
        setConnectionStatus('disconnected');
        throw new Error('Room object is null after connection attempt');
      }
      
      // Clear connection timeout since we connected successfully
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      connectionStartTimeRef.current = null;
      
      // Set room and status immediately - connection successful
      setTwilioRoom(room);
      setConnectionStatus('connected');
      setIsCallEnded(false); // Reset call ended state on successful connection
      // Keep hasConnectedRef.current = true (already set at start)
      
      console.log('✅ Successfully connected to Twilio room:', tokenResponse.room_name);
      
      // Add a small delay to check if connection is stable
      setTimeout(() => {
        if (hasConnectedRef.current) {
          console.log('✅ Connection stable after 1 second');
        } else {
          console.warn('⚠️ Connection may have been lost immediately after connect');
        }
      }, 1000);

      // Helper: handle remote participant (covers both "already in room" and "just connected")
      const handleRemoteParticipant = async (participant: TwilioVideo.RemoteParticipant) => {
        console.log('👤 Handling remote participant:', participant.identity);
        setHasRemoteParticipant(true);

        // Notify backend that participant joined (update call status to accepted if not already)
        const currentCallRequest = callRequestRef.current;
        if (currentCallRequest && currentCallRequest.status !== 'accepted' && currentCallRequest.status !== 'completed') {
          try {
            await videoConferenceService.updateCallStatus(currentCallRequest.id, 'accepted');
            console.log('✅ Updated call status to accepted after participant joined');
            setCallRequest({ ...currentCallRequest, status: 'accepted' });
          } catch (err) {
            console.error('Failed to update call status when participant joined:', err);
          }
        }

        // Existing video tracks
        participant.videoTracks.forEach((publication: TwilioVideo.RemoteVideoTrackPublication) => {
          if (publication.track) {
            console.log('Found existing video track from participant');
            setRemoteVideoTrack(publication.track);
          }
        });

        // Existing audio tracks (guarded for SDK differences)
        try {
          const audioTracks = (participant as any).audioTracks;
          if (audioTracks && typeof audioTracks.forEach === 'function') {
            audioTracks.forEach((publication: any) => {
              if (publication && publication.track && publication.track.kind === 'audio') {
                console.log('Found existing audio track from participant');
                setRemoteAudioTrack(publication.track);
              }
            });
          }
        } catch (err) {
          console.log('audioTracks not available, will rely on trackSubscribed event');
        }

        // Listen for future track events (idempotent per participant instance)
        participant.on('trackSubscribed', (track: TwilioVideo.RemoteTrack) => {
          console.log('Track subscribed:', track.kind);
          if (track.kind === 'video') {
            setRemoteVideoTrack(track as TwilioVideo.RemoteVideoTrack);
            console.log('Remote video track set, will attach via useEffect');
          } else if (track.kind === 'audio') {
            setRemoteAudioTrack(track);
            console.log('Remote audio track set, will attach via useEffect');
          }
        });

        participant.on('trackUnsubscribed', (track: TwilioVideo.RemoteTrack) => {
          console.log('Track unsubscribed:', track.kind);
          if (track.kind === 'video') {
            setRemoteVideoTrack(null);
          } else if (track.kind === 'audio') {
            setRemoteAudioTrack(null);
          }
        });
      };

      // Set up room event listeners
      room.on('participantConnected', async (participant: TwilioVideo.RemoteParticipant) => {
        console.log('Participant connected:', participant.identity);
        await handleRemoteParticipant(participant);
      });

      // CRITICAL: if patient joined before doctor, Twilio will NOT emit participantConnected.
      // Handle existing participants immediately on connect.
      const participants = (room as any).participants as Map<string, TwilioVideo.RemoteParticipant> | undefined;
      if (participants && participants.size > 0) {
        console.log('👥 Found existing participants on connect:', participants.size);
        participants.forEach((participant: TwilioVideo.RemoteParticipant) => {
          handleRemoteParticipant(participant);
        });
      }

      room.on('participantDisconnected', (participant: TwilioVideo.RemoteParticipant) => {
        console.log('Participant disconnected:', participant.identity);
        // If multiple participants ever become a thing, recompute based on remaining participants.
        const participants = (room as any).participants as Map<string, TwilioVideo.RemoteParticipant> | undefined;
        const remaining = participants ? participants.size : 0;
        setHasRemoteParticipant(remaining > 0);
        if (remaining <= 0) {
          setRemoteVideoTrack(null);
          setRemoteAudioTrack(null);
        }
      });

      room.on('reconnecting', (error: any) => {
        console.log('🔄 Room is reconnecting:', error);
        setConnectionStatus('connecting');
      });

      room.on('reconnected', () => {
        console.log('✅ Room reconnected successfully');
        setConnectionStatus('connected');
        setIsCallEnded(false);
        setPermissionError(null);
      });

      room.on('disconnected', (reason?: any) => {
        console.log('⚠️ Disconnected from room:', reason);
        console.error('Disconnection details:', {
          reason: reason?.message || reason,
          code: reason?.code,
          name: reason?.name,
          twilioError: reason
        });
        
        // Only stop tracks if call was intentionally ended, not if it's a network disconnection
        // This allows reconnection without losing track state
        const callWasEnded = isCallEnded;
        if (callWasEnded) {
          // Stop local tracks when call is intentionally ended
          console.log('🛑 Stopping local tracks due to call end...');
        if (videoTrack) {
          try {
            if (localVideoRef.current) {
              videoTrack.detach(localVideoRef.current);
            }
            videoTrack.stop();
          } catch (e) {
            console.error('Error stopping video track on disconnect:', e);
          }
        }
        if (audioTrack) {
          try {
            audioTrack.stop();
          } catch (e) {
            console.error('Error stopping audio track on disconnect:', e);
          }
        }
        // Clear video element srcObject
        if (localVideoRef.current) {
          try {
            const stream = localVideoRef.current.srcObject as MediaStream;
            if (stream) {
              stream.getTracks().forEach(track => track.stop());
            }
            localVideoRef.current.srcObject = null;
          } catch (e) {
            console.error('Error clearing video element on disconnect:', e);
          }
        }
        setLocalVideoTrack(null);
        setLocalAudioTrack(null);
        } else {
          // Network disconnection - keep tracks for reconnection
          console.log('🔄 Network disconnection detected, keeping tracks for reconnection');
        }
        
        setTwilioRoom(null);
        setHasRemoteParticipant(false);
        setRemoteVideoTrack(null);
        // Only update connection status if call wasn't intentionally ended
        // (if call was ended, status is already set to disconnected)
        if (!callWasEnded) {
        setConnectionStatus('disconnected');
        }
        hasConnectedRef.current = false;
        
        // Show error message if disconnection was unexpected
        if (reason) {
          let errorMsg = 'Connection lost. ';
          if (reason.message?.includes('token') || reason.code === 20101) {
            errorMsg += 'Token expired or invalid. Please refresh the page.';
          } else if (reason.message?.includes('network') || reason.code === 20108) {
            errorMsg += 'Network error. Please check your connection.';
          } else if (reason.code === 20103) {
            errorMsg += 'Room not found or expired.';
          } else {
            errorMsg += reason.message || 'Please try reconnecting.';
          }
          setPermissionError(errorMsg);
        }
      });

      console.log('Connected to Twilio room:', tokenResponse.room_name);
    } catch (err: any) {
      console.error('Failed to connect to Twilio room:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      
      let errorMessage = 'Failed to connect to video call. Please try again.';
      if (err.message?.includes('token')) {
        errorMessage = 'Invalid or expired token. Please refresh the page.';
      } else if (err.message?.includes('room')) {
        errorMessage = 'Unable to join the video room. Please check your connection.';
      }
      
      setPermissionError(errorMessage);
      setConnectionStatus('disconnected');
      
              // Clean up tracks on error
              if (videoTrack) {
        try {
                videoTrack.stop();
                videoTrack.detach();
        } catch (cleanupErr) {
          console.error('Error cleaning up video track:', cleanupErr);
        }
              }
              if (audioTrack) {
        try {
                audioTrack.stop();
        } catch (cleanupErr) {
          console.error('Error cleaning up audio track:', cleanupErr);
              }
      }
      setLocalVideoTrack(null);
      setLocalAudioTrack(null);
              hasConnectedRef.current = false;
            }
          }, [isCallEnded]);

  // Stop local tracks and disconnect from room
  const stopLocalTracks = useCallback(() => {
    console.log('🛑 Stopping local tracks and releasing camera...');
    
    // Stop and detach video track first
    if (localVideoTrack) {
      try {
        console.log('🛑 Stopping local video track...');
        // Detach from all elements first
        if (localVideoRef.current) {
          try {
            localVideoTrack.detach(localVideoRef.current);
          } catch (e) {
            console.warn('Error detaching video track from ref:', e);
          }
        }
        // Stop the track to release camera
      localVideoTrack.stop();
        console.log('✅ Local video track stopped');
      } catch (err) {
        console.error('Error stopping video track:', err);
      }
      setLocalVideoTrack(null);
    }
    
    // Stop audio track
    if (localAudioTrack) {
      try {
        console.log('🛑 Stopping local audio track...');
      localAudioTrack.stop();
        console.log('✅ Local audio track stopped');
      } catch (err) {
        console.error('Error stopping audio track:', err);
      }
      setLocalAudioTrack(null);
    }
    
    // Clear video element srcObject to release media stream
    if (localVideoRef.current) {
      try {
        console.log('🛑 Clearing local video element srcObject...');
        const stream = localVideoRef.current.srcObject as MediaStream;
        if (stream) {
          // Stop all tracks in the stream
          stream.getTracks().forEach(track => {
            console.log('🛑 Stopping media track:', track.kind);
            track.stop();
          });
        }
      localVideoRef.current.srcObject = null;
        console.log('✅ Local video element cleared');
      } catch (err) {
        console.error('Error clearing local video element:', err);
    }
    }
    
    if (remoteVideoRef.current) {
      try {
      remoteVideoRef.current.srcObject = null;
      } catch (err) {
        console.error('Error clearing remote video element:', err);
    }
    }
    
    console.log('✅ All local tracks stopped and camera released');
  }, [localVideoTrack, localAudioTrack]);

  // Attach remote audio track to audio element
  useEffect(() => {
    if (!remoteAudioTrack || !remoteAudioRef.current) {
      return;
    }

    const element = remoteAudioRef.current;
    console.log('🎵 Attaching remote audio track to audio element');

    try {
      // Detach from any previous element first
      if (element.srcObject) {
        console.log('Detaching remote audio from previous element');
        remoteAudioTrack.detach();
      }
      
      // Attach the track to the audio element
      remoteAudioTrack.attach(element);
      console.log('✅ Remote audio track attached successfully');
      
      // Ensure audio is enabled and volume is set
      if (element) {
        element.volume = 1.0;
        element.muted = false;
        console.log('✅ Remote audio element configured:', {
          volume: element.volume,
          muted: element.muted,
          paused: element.paused
        });
      }
    } catch (err) {
      console.error('❌ Error attaching remote audio track:', err);
    }

    // Cleanup function
    return () => {
      if (remoteAudioTrack && element) {
        try {
          console.log('🧹 Detaching remote audio track on cleanup');
          remoteAudioTrack.detach();
          element.srcObject = null;
        } catch (cleanupErr) {
          console.error('Error detaching remote audio track:', cleanupErr);
        }
      }
    };
  }, [remoteAudioTrack]);

  const loadClinicalData = useCallback(async (callId: number) => {
    try {
      const data = await videoConferenceService.getClinicalData(callId);
      setClinicalData(data);
      setEditedSummary(data.summary);
    } catch (err: any) {
      console.error('Failed to load clinical data:', err);
    }
  }, []);

  const loadChatMessages = useCallback(async (callId: number, clearProcessingIfAIResponded: boolean = true) => {
    try {
      // First, create or get chat session to avoid 404 errors
      const chatSession = await videoConferenceService.createOrGetChatSession(callId);
      setChatSessionId(chatSession.session_id);
      // Then load messages
      const response = await videoConferenceService.getChatMessages(callId);
      const messages = response.messages || [];
      setChatMessages(messages);
      
      // Clear processing state if we have AI agent messages and user is waiting for response
      if (clearProcessingIfAIResponded && messages.length > 0) {
        // Check if the last message is from AI agent
        const lastMessage = messages[messages.length - 1];
        const isLastMessageFromAI = lastMessage.sender_type === 'ai_agent' || lastMessage.role === 'assistant';
        if (isLastMessageFromAI) {
          console.log('✅ [loadChatMessages] AI agent response found, clearing processing state');
          setIsAgentProcessing(false);
        }
      }
    } catch (err: any) {
      console.error('Failed to load chat messages:', err);
      setChatMessages([]);
      // Clear processing state on error
      setIsAgentProcessing(false);
    }
  }, []);

  // Connect to WebSocket for real-time transcription updates
  const connectToChatWebSocket = useCallback((callId: number, sessionId: number | null = null) => {
    // Close existing WebSocket if any
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }

    // Clear any pending reconnect timeout
    if (websocketReconnectTimeoutRef.current) {
      clearTimeout(websocketReconnectTimeoutRef.current);
      websocketReconnectTimeoutRef.current = null;
    }

    // Use provided sessionId or get from state
    const sessionIdToUse = sessionId || chatSessionId;
    
    // Wait for chat session ID to be available
    if (!sessionIdToUse) {
      console.log('⏳ [WebSocket] Waiting for chat session ID...');
      // Retry after a short delay
      setTimeout(() => {
        if (callRequest?.id) {
          connectToChatWebSocket(callRequest.id, chatSessionId);
        }
      }, 500);
      return;
    }

    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const { url: wsUrl, sendFirstMessage } = getChatStatusWebSocketUrl(sessionIdToUse, apiBase);

      console.log('🔌 [WebSocket] Connecting to chat session WebSocket:', wsUrl, 'for call', callId, 'session', sessionIdToUse);

      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;

      ws.onopen = () => {
        console.log('✅ [WebSocket] Connected to chat session WebSocket for session', sessionIdToUse);
        if (sendFirstMessage) {
          ws.send(JSON.stringify({ session_id: sessionIdToUse }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const statusMessage = JSON.parse(event.data);
          console.log('📨 [WebSocket] Received status message:', statusMessage);

          // Backend sends ChatStatusMessage format: { session_id, status, message (JSON string), progress, agent_name }
          // The actual event data is in the 'message' field as a JSON string
          let eventData = null;
          if (statusMessage.message) {
            try {
              eventData = typeof statusMessage.message === 'string' 
                ? JSON.parse(statusMessage.message) 
                : statusMessage.message;
            } catch (e) {
              console.warn('⚠️ [WebSocket] Failed to parse nested message field:', e);
            }
          }

          // Handle status field (connection status, transcription events, etc.)
          const status = statusMessage.status;
          if (status === 'transcription_complete' || (eventData && eventData.type === 'transcription_complete')) {
            console.log('✅ [WebSocket] Transcription complete received for call', callId);
            // Processing is complete, reset processing state
            setIsProcessing(false);
            // Reload clinical data to show new summary, prescriptions, lab tests
            loadClinicalData(callId);
          } else if (status === 'transcription_progress' || (eventData && eventData.type === 'transcription_progress')) {
            const progressData = eventData || statusMessage;
            console.log('📊 [WebSocket] Transcription progress:', progressData.stage, progressData.progress);
            // Ensure we're in processing state during progress updates
            setIsProcessing(true);
          } else if (status === 'transcription_error' || (eventData && eventData.type === 'transcription_error')) {
            const errorData = eventData || statusMessage;
            console.error('❌ [WebSocket] Transcription error:', errorData.error);
            // Reset processing state on error
            setIsProcessing(false);
            // Optionally show error in UI
          } else if (status === 'processing' || (eventData && eventData.type === 'processing')) {
            console.log('🔄 [WebSocket] AI agent is processing request');
            // Set processing state when AI agent starts processing
            if (selectedRecipient === 'ai_agent') {
              setIsAgentProcessing(true);
            }
          } else if (status === 'new_message' || (eventData && eventData.type === 'new_message') || status === 'message_added') {
            console.log('📨 [WebSocket] Message notification received', { status, eventData, fullMessage: statusMessage });
            
            // Extract message_id from notification (lightweight notification approach)
            let messageId: number | null = null;
            if (eventData) {
              // New format: lightweight notification with just message_id
              if (eventData.message_id) {
                messageId = typeof eventData.message_id === 'number' ? eventData.message_id : parseInt(eventData.message_id, 10);
                console.log('📨 [WebSocket] Received lightweight notification for message_id:', messageId);
              }
              // Legacy format support: check if full message data exists (for backward compatibility)
              else if (eventData.new_message && eventData.new_message.id) {
                messageId = typeof eventData.new_message.id === 'number' ? eventData.new_message.id : parseInt(eventData.new_message.id, 10);
                console.log('📨 [WebSocket] Received legacy format with message_id:', messageId);
              }
            }
            
            // Clear processing state when AI agent responds
            if (isAgentProcessing && selectedRecipient === 'ai_agent') {
              console.log('✅ [WebSocket] Clearing processing state on message notification');
                setIsAgentProcessing(false);
              }
            
            // Debounce reload to avoid multiple rapid reloads
            if (reloadDebounceTimeoutRef.current) {
              clearTimeout(reloadDebounceTimeoutRef.current);
            }
            
            // Reload messages from backend (single source of truth)
            // Small delay ensures message is persisted in DB
            reloadDebounceTimeoutRef.current = setTimeout(() => {
              console.log('📨 [WebSocket] Reloading chat messages from backend (message_id:', messageId, ')');
            loadChatMessages(callId);
              reloadDebounceTimeoutRef.current = null;
            }, 300);
          } else if (status === 'clinical_data_updated' || (eventData && eventData.type === 'clinical_data_updated')) {
            console.log('🔄 [WebSocket] Clinical data updated for call', callId);
            // Reload clinical data
            loadClinicalData(callId);
          } else if (status === 'complete' || (eventData && eventData.type === 'complete')) {
            console.log('✅ [WebSocket] Agent processing complete');
            // Clear processing state when agent completes
            if (isAgentProcessing && selectedRecipient === 'ai_agent') {
              console.log('✅ [WebSocket] Clearing processing state on complete status');
              setIsAgentProcessing(false);
              // Note: We don't reload messages here because the message was already added
              // via the "message_added" status. Reloading here would cause duplicates.
              // The "complete" status is just a signal that processing is done.
            }
          } else if (status === 'heartbeat' || status === 'connected') {
            // Ignore heartbeat and connection status messages
          } else {
            console.log('📨 [WebSocket] Unknown status:', status, 'eventData:', eventData);
          }
        } catch (err) {
          console.error('❌ [WebSocket] Failed to parse message:', err, event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ [WebSocket] WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('🔌 [WebSocket] WebSocket closed:', event.code, event.reason);
        websocketRef.current = null;

        // Keep WebSocket open even after call ends - only close when appointment is marked as complete
        // Use ref to get current callRequest state and appointment completion status
        const currentCallRequest = callRequestRef.current;
        const isAppointmentCompleted = appointmentCompletedRef.current;
        
        // Reconnect if appointment is not marked as complete (even if call has ended)
        // This allows receiving messages after the call ends until "Mark as Complete" is clicked
        const shouldReconnect = !isAppointmentCompleted && currentCallRequest && chatSessionId;
        
        if (shouldReconnect) {
          console.log('🔄 [WebSocket] Attempting to reconnect in 3 seconds... (call may have ended, but appointment not completed)');
          websocketReconnectTimeoutRef.current = setTimeout(() => {
            // Reconnect with current sessionId from state
            const currentSessionId = chatSessionId;
            if (callId && currentSessionId) {
              connectToChatWebSocket(callId, currentSessionId);
            }
          }, 3000);
        } else {
          console.log('🔌 [WebSocket] Not reconnecting - appointment completed or no session ID');
        }
      };
    } catch (err) {
      console.error('❌ [WebSocket] Failed to create WebSocket connection:', err);
    }
  }, [chatSessionId, callRequest, loadClinicalData, loadChatMessages, isAgentProcessing, selectedRecipient]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }
      if (websocketReconnectTimeoutRef.current) {
        clearTimeout(websocketReconnectTimeoutRef.current);
        websocketReconnectTimeoutRef.current = null;
      }
    };
  }, []);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!callRequest || !newMessage.trim()) return;
    try {
      // If sending to AI agent, set processing state
      if (selectedRecipient === 'ai_agent') {
        setIsAgentProcessing(true);
      }
      
      await videoConferenceService.sendChatMessage(
        callRequest.id,
        newMessage.trim(),
        selectedRecipient,
        'doctor'
      );
      setNewMessage('');
      // Reload messages to show the new one
      await loadChatMessages(callRequest.id);
    } catch (err: any) {
      console.error('Failed to send message:', err);
      setIsAgentProcessing(false); // Reset on error
      alert(err.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleEditPrescription = (prescription: ClinicalDataResponse['prescriptions'][0]) => {
    setEditingPrescriptionId(prescription.id);
    setEditPrescriptionForm({
      medication_name: prescription.medication_name,
      dosage: prescription.dosage || '',
      frequency: prescription.frequency || '',
      instructions: prescription.instructions || '',
      duration: prescription.duration || '',
    });
  };

  const handleSavePrescription = async () => {
    if (!callRequest || !editingPrescriptionId) return;
    try {
      await videoConferenceService.updatePrescription(
        callRequest.id,
        editingPrescriptionId,
        editPrescriptionForm
      );
      setEditingPrescriptionId(null);
      // Reload clinical data to show updated prescription
      await loadClinicalData(callRequest.id);
    } catch (err: any) {
      console.error('Failed to update prescription:', err);
      alert(err.response?.data?.detail || 'Failed to update prescription');
    }
  };

  const handleDeletePrescription = async (prescriptionId: string) => {
    if (!callRequest) return;
    if (!window.confirm('Are you sure you want to delete this prescription?')) return;
    try {
      await videoConferenceService.deletePrescription(callRequest.id, prescriptionId);
      // Reload clinical data to show updated list
      await loadClinicalData(callRequest.id);
    } catch (err: any) {
      console.error('Failed to delete prescription:', err);
      alert(err.response?.data?.detail || 'Failed to delete prescription');
    }
  };

  const handleAddPrescription = () => {
    setIsAddingPrescription(true);
    setEditPrescriptionForm({
      medication_name: '',
      dosage: '',
      frequency: '',
      instructions: '',
      duration: '',
    });
  };

  const handleCreatePrescription = async () => {
    if (!callRequest || !editPrescriptionForm.medication_name.trim()) {
      alert('Please enter a medication name');
      return;
    }
    try {
      await videoConferenceService.createPrescription(callRequest.id, editPrescriptionForm);
      setIsAddingPrescription(false);
      setEditPrescriptionForm({
        medication_name: '',
        dosage: '',
        frequency: '',
        instructions: '',
        duration: '',
      });
      // Reload clinical data to show new prescription
      await loadClinicalData(callRequest.id);
    } catch (err: any) {
      console.error('Failed to create prescription:', err);
      alert(err.response?.data?.detail || 'Failed to create prescription');
    }
  };

  const handleEditLabTest = (labTest: ClinicalDataResponse['lab_tests'][0]) => {
    setEditingLabTestId(labTest.id);
    setEditLabTestForm({
      test_name: labTest.test_name,
      test_category: labTest.test_category || '',
      reason: labTest.reason || '',
      priority: labTest.priority || '',
      instructions: labTest.instructions || '',
      notes: labTest.notes || '',
    });
  };

  const handleSaveLabTest = async () => {
    if (!callRequest || !editingLabTestId) return;
    try {
      await videoConferenceService.updateLabTest(
        callRequest.id,
        editingLabTestId,
        editLabTestForm
      );
      setEditingLabTestId(null);
      // Reload clinical data to show updated lab test
      await loadClinicalData(callRequest.id);
    } catch (err: any) {
      console.error('Failed to update lab test:', err);
      alert(err.response?.data?.detail || 'Failed to update lab test');
    }
  };

  const handleDeleteLabTest = async (labTestId: number) => {
    if (!callRequest) return;
    if (!window.confirm('Are you sure you want to delete this lab test?')) return;
    try {
      await videoConferenceService.deleteLabTest(callRequest.id, labTestId);
      // Reload clinical data to show updated list
      await loadClinicalData(callRequest.id);
    } catch (err: any) {
      console.error('Failed to delete lab test:', err);
      alert(err.response?.data?.detail || 'Failed to delete lab test');
    }
  };

  const handleAddLabTest = () => {
    setIsAddingLabTest(true);
    setEditLabTestForm({
      test_name: '',
      test_category: '',
      reason: '',
      priority: '',
      instructions: '',
      notes: '',
    });
  };

  const handleCreateLabTest = async () => {
    if (!callRequest || !editLabTestForm.test_name.trim()) {
      alert('Please enter a test name');
      return;
    }
    try {
      await videoConferenceService.createLabTest(callRequest.id, editLabTestForm);
      setIsAddingLabTest(false);
      setEditLabTestForm({
        test_name: '',
        test_category: '',
        reason: '',
        priority: '',
        instructions: '',
        notes: '',
      });
      // Reload clinical data to show new lab test
      await loadClinicalData(callRequest.id);
    } catch (err: any) {
      console.error('Failed to create lab test:', err);
      alert(err.response?.data?.detail || 'Failed to create lab test');
    }
  };

  const loadAppointmentAndCallRequest = useCallback(async () => {
    if (!appointmentId) return;

    try {
      setLoading(true);
      setError(null);

      const [apptData, callRequestData] = await Promise.all([
        dashboardService.getAppointmentById(parseInt(appointmentId)),
        videoConferenceService.getCallRequestForAppointment(parseInt(appointmentId)).catch(() => null),
      ]);

      setAppointment(apptData);
      setCallRequest(callRequestData);
      
      // Update appointment completion ref based on appointment status
      appointmentCompletedRef.current = apptData.status === 'completed';

      // If call request exists, load clinical data and chat
      if (callRequestData) {
        await Promise.all([
          loadClinicalData(callRequestData.id),
          loadChatMessages(callRequestData.id),
        ]);
        // Connect to WebSocket for real-time transcription updates (after chat session is created)
        // The chatSessionId will be set by loadChatMessages, so we'll connect via useEffect
      }

      // Start call timer and connect to Twilio room if call is active
      // Allow connection for 'pending' and 'accepted' statuses
      // Do NOT allow connection if appointment is completed
      const isAppointmentCompleted = apptData.status === 'completed';
      const canConnect = callRequestData && 
        !isAppointmentCompleted &&
        (callRequestData.status === 'accepted' || callRequestData.status === 'pending') &&
        !hasConnectedRef.current;
        
      if (canConnect) {
        console.log('📞 Call request is active, starting connection...', {
          callId: callRequestData.id,
          channelName: callRequestData.channel_name,
          doctorId: callRequestData.doctor_id,
          status: callRequestData.status
        });
        startCallTimer();
        // Connect to Twilio room (this will also request permissions)
        await connectToTwilioRoom(callRequestData.channel_name, callRequestData.doctor_id);
      } else {
        console.log('⏸️ Not connecting to room:', {
          hasCallRequest: !!callRequestData,
          status: callRequestData?.status,
          isAppointmentCompleted,
          alreadyConnected: hasConnectedRef.current,
          canConnect: canConnect
        });
        
        if (isAppointmentCompleted) {
          console.log('🚫 Cannot connect - appointment is already completed');
          setError('This appointment has been completed. Video calls are no longer available.');
          setConnectionStatus('disconnected');
        } else if (callRequestData && callRequestData.status !== 'accepted' && callRequestData.status !== 'pending') {
        // If call exists but status prevents auto-connect, set connection status
          setConnectionStatus('disconnected');
        }
      }
    } catch (err: any) {
      console.error('Failed to load appointment:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load appointment');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, loadClinicalData, loadChatMessages, connectToTwilioRoom]);

  useEffect(() => {
    // Only load once on mount or when appointmentId changes
    // Using appointmentId directly to avoid infinite loops from callback dependencies
    if (appointmentId) {
      loadAppointmentAndCallRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  // Connect to WebSocket when chat session ID is available
  useEffect(() => {
    if (chatSessionId && callRequest?.id) {
      console.log('🔌 [WebSocket] Chat session ID available, connecting WebSocket...', chatSessionId);
      connectToChatWebSocket(callRequest.id, chatSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessionId, callRequest?.id]);

  // Connect to WebSocket when chat session ID is available
  useEffect(() => {
    if (chatSessionId && callRequest?.id) {
      console.log('🔌 [WebSocket] Chat session ID available, connecting WebSocket...', chatSessionId);
      connectToChatWebSocket(callRequest.id, chatSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessionId, callRequest?.id]);

  const startCallTimer = () => {
    callStartTimeRef.current = new Date();
    durationIntervalRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        const elapsed = Math.floor((new Date().getTime() - callStartTimeRef.current.getTime()) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        setCallDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);
  };

  const stopCallTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  const handleMute = () => {
    if (localAudioTrack) {
      localAudioTrack.enable(!isMuted);
    }
    setIsMuted(!isMuted);
  };

  const handleToggleCamera = () => {
    const newCameraState = !isCameraEnabled;
    if (localVideoTrack) {
      localVideoTrack.enable(newCameraState);
      console.log('Camera toggled:', newCameraState);
    }
    setIsCameraEnabled(newCameraState);
  };

  const handleStartRecording = async () => {
    if (!callRequest) return;
    try {
      setIsRecording(true); // Set optimistically for better UX
      await videoConferenceService.startRecording(callRequest.id);
      console.log('✅ Recording started successfully');
    } catch (err: any) {
      console.error('Failed to start recording:', err);
      setIsRecording(false); // Revert on error
      
      // Extract error message
      let errorMessage = 'Failed to start recording.';
      const errorDetail = err.response?.data?.detail || err.message || '';
      
      if (errorDetail.includes('S3') || errorDetail.includes('s3')) {
        errorMessage = 'Recording service is not configured. Please contact support or check server configuration.';
      } else if (errorDetail.includes('Service') && errorDetail.includes('not enabled')) {
        errorMessage = 'Recording service is not enabled. Please check server configuration.';
      } else if (errorDetail) {
        errorMessage = errorDetail;
      }
      
      alert(errorMessage);
    }
  };

  const handleStopRecording = async () => {
    if (!callRequest) return;
    try {
      // Set processing state immediately when stop is clicked
      setIsRecording(false);
      setIsProcessing(true);
      
      await videoConferenceService.stopRecording(callRequest.id);
      console.log('✅ Recording stopped, waiting for transcription to complete...');
      // Don't set isProcessing to false here - wait for WebSocket message about transcription completion
      // The WebSocket handler will set isProcessing to false when transcription_complete is received
    } catch (err: any) {
      console.error('Failed to stop recording:', err);
      setIsProcessing(false); // Revert processing state on error
      alert(err.response?.data?.detail || 'Failed to stop recording');
    }
  };

  const handleEndCall = async () => {
    if (!callRequest) return;
    try {
      console.log('📞 Ending call...');
      stopCallTimer();
      
      // IMPORTANT: Stop local tracks FIRST to release camera/microphone
      // before disconnecting from room
      console.log('🛑 Stopping local tracks to release camera/microphone...');
      stopLocalTracks();
      
      // Also stop remote tracks and clear remote video
      if (remoteVideoTrack) {
        try {
          console.log('🛑 Stopping remote video track...');
          if (remoteVideoRef.current) {
            remoteVideoTrack.detach(remoteVideoRef.current);
          }
          setRemoteVideoTrack(null);
        } catch (err) {
          console.error('Error stopping remote video track:', err);
        }
      }
      
      if (remoteAudioTrack) {
        try {
          console.log('🛑 Stopping remote audio track...');
          remoteAudioTrack.detach();
          setRemoteAudioTrack(null);
        } catch (err) {
          console.error('Error stopping remote audio track:', err);
        }
      }
      
      // Clear remote video element
      if (remoteVideoRef.current) {
        try {
          remoteVideoRef.current.srcObject = null;
        } catch (err) {
          console.error('Error clearing remote video element:', err);
        }
      }
      
      // Reset connection state
      hasConnectedRef.current = false;
      setConnectionStatus('disconnected');
      setIsCallEnded(true);
      setHasRemoteParticipant(false);
      
      // Disconnect from Twilio room (but keep WebSocket open for chat messages)
      if (twilioRoom) {
        console.log('📞 Disconnecting from Twilio room...');
        try {
        twilioRoom.disconnect();
          console.log('✅ Disconnected from Twilio room');
        } catch (err) {
          console.error('Error disconnecting from room:', err);
        }
        setTwilioRoom(null);
      }
      
      // Update call status to completed
      const updatedCallRequest = await videoConferenceService.updateCallStatus(callRequest.id, 'completed');
      // Update callRequest state so ref is updated
      if (updatedCallRequest) {
        setCallRequest(updatedCallRequest);
      }
      console.log('✅ Call ended successfully. WebSocket remains open for chat messages until appointment is marked as complete.');
      // Don't navigate away - stay on the same page
      // IMPORTANT: Do NOT close WebSocket here - keep it open to receive messages after call ends
    } catch (err: any) {
      console.error('Failed to end call:', err);
      // Even if update fails, ensure tracks are stopped
      stopLocalTracks();
      setIsCallEnded(true);
      setConnectionStatus('disconnected');
      alert(err.response?.data?.detail || 'Failed to end call');
    }
  };

  const handleReconnect = async () => {
    if (!callRequest || !appointment) return;
    
    // Prevent reconnection if appointment is completed
    if (appointment.status === 'completed') {
      console.log('🚫 Cannot reconnect - appointment is already completed');
      alert('This appointment has been completed. Video calls are no longer available.');
      return;
    }
    
    try {
      console.log('🔄 Reconnecting to call...');
      setIsCallEnded(false);
      setConnectionStatus('connecting');
      hasConnectedRef.current = false;
      
      // Reset remote participant state
      setHasRemoteParticipant(false);
      setRemoteVideoTrack(null);
      setRemoteAudioTrack(null);
      
      // Reconnect to Twilio room
      await connectToTwilioRoom(callRequest.channel_name, callRequest.doctor_id);
      
      // Restart call timer if call was previously active
      if (callRequest.status === 'accepted' || callRequest.status === 'pending') {
        startCallTimer();
      }
    } catch (err: any) {
      console.error('Failed to reconnect:', err);
      setConnectionStatus('disconnected');
      setIsCallEnded(true);
      alert(err.response?.data?.detail || 'Failed to reconnect to call');
    }
  };

  const handleMarkAsComplete = async () => {
    if (!appointment || !appointmentId) return;
    
    // Confirm action
    if (!window.confirm('Are you sure you want to mark this appointment as complete? This will end the video call.')) {
      return;
    }
    
    setIsMarkingComplete(true);
    try {
      // First, end the video call if it's active
      if (twilioRoom && !isCallEnded) {
        console.log('📞 Ending video call before marking appointment as complete...');
        await handleEndCall();
      }
      
      const updatedAppointment = await dashboardService.updateAppointment(
        parseInt(appointmentId),
        { status: 'completed' }
      );
      
      setAppointment(updatedAppointment);
      
      // Mark appointment as completed - this will prevent WebSocket reconnection
      appointmentCompletedRef.current = true;
      
      // Close WebSocket connection now that appointment is marked as complete
      if (websocketRef.current) {
        console.log('🔌 [WebSocket] Closing WebSocket connection - appointment marked as complete');
        websocketRef.current.close();
        websocketRef.current = null;
      }
      
      // Clear any pending reconnect timeout
      if (websocketReconnectTimeoutRef.current) {
        clearTimeout(websocketReconnectTimeoutRef.current);
        websocketReconnectTimeoutRef.current = null;
      }
      
      // Ensure all video resources are cleaned up
      stopLocalTracks();
      
      alert('Appointment marked as complete successfully!');
    } catch (err: any) {
      console.error('Failed to mark appointment as complete:', err);
      alert(err.response?.data?.detail || 'Failed to mark appointment as complete');
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const handleSaveSummary = async () => {
    if (!callRequest || !editedSummary) return;
    try {
      await videoConferenceService.updateClinicalSummary(callRequest.id, editedSummary);
      setIsEditingSummary(false);
      await loadClinicalData(callRequest.id);
    } catch (err: any) {
      console.error('Failed to save summary:', err);
      alert(err.response?.data?.detail || 'Failed to save summary');
    }
  };

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  const sidebarItems = createSidebarItems(navigate, location.pathname);

  // Attach local video track when ref is ready (backup to callback ref)
  useEffect(() => {
    if (localVideoTrack && localVideoRef.current) {
      const videoElement = localVideoRef.current;
      
      console.log('🔄 useEffect: Attaching local video track', {
        cameraEnabled: isCameraEnabled,
        refReady: !!localVideoRef.current,
        elementVisible: videoElement.offsetWidth > 0 && videoElement.offsetHeight > 0,
        hasSrcObject: !!videoElement.srcObject
      });
      
      try {
        // Detach first if already attached
        if (videoElement.srcObject) {
          console.log('Detaching from existing srcObject');
          localVideoTrack.detach(videoElement);
        }
        
        // Attach the track
        localVideoTrack.attach(videoElement);
        console.log('✅ Track attached via useEffect');
        
        // Verify attachment by checking srcObject
        setTimeout(() => {
          if (videoElement.srcObject) {
            console.log('✅ Video element has srcObject - stream is attached');
            console.log('Video element details:', {
              videoWidth: videoElement.videoWidth,
              videoHeight: videoElement.videoHeight,
              readyState: videoElement.readyState,
              paused: videoElement.paused,
              muted: videoElement.muted
            });
          } else {
            console.error('❌ Video element does NOT have srcObject after attachment!');
          }
        }, 200);
        
        // Enable or disable the track based on camera state
        if (localVideoTrack.enable) {
          localVideoTrack.enable(isCameraEnabled);
          console.log('✅ Track enabled:', isCameraEnabled);
        }
        
        console.log('✅ Local video track attached via useEffect');
      } catch (err) {
        console.error('❌ Error in useEffect attaching local video track:', err);
      }
      
    return () => {
        if (localVideoRef.current && localVideoTrack) {
          try {
            console.log('🧹 Detaching local video track in cleanup');
            localVideoTrack.detach(localVideoRef.current);
          } catch (err) {
            console.error('❌ Error detaching in cleanup:', err);
          }
        }
      };
    } else {
      console.log('⏳ Local video track attachment waiting:', {
        hasTrack: !!localVideoTrack,
        hasRef: !!localVideoRef.current
      });
    }
  }, [localVideoTrack, isCameraEnabled]);

  // Attach remote video track when ref is ready
  useEffect(() => {
    if (remoteVideoTrack && remoteVideoRef.current) {
      const videoElement = remoteVideoRef.current;
      console.log('Attaching remote video track to element', {
        trackKind: remoteVideoTrack.kind,
        elementReady: !!videoElement
      });
      
      try {
        // Detach first if already attached
        if (videoElement.srcObject) {
          remoteVideoTrack.detach(videoElement);
        }
        
        remoteVideoTrack.attach(videoElement);
        console.log('Remote video track attached successfully');
      } catch (err) {
        console.error('Error attaching remote video track:', err);
      }
      
      return () => {
        // Capture the element reference at cleanup time
        const elementToCleanup = videoElement;
        if (elementToCleanup && remoteVideoTrack) {
          try {
            remoteVideoTrack.detach(elementToCleanup);
          } catch (err) {
            console.error('Error detaching remote track:', err);
          }
        }
      };
    }
  }, [remoteVideoTrack]);

  // Cleanup on unmount - only disconnect when component unmounts
  useEffect(() => {
    return () => {
      console.log('🧹 Component unmounting, cleaning up...');
      stopCallTimer();
      
      // Disconnect from Twilio room only on unmount
      if (twilioRoom) {
        console.log('🧹 Disconnecting from room on unmount');
        twilioRoom.disconnect();
      }
      
      // Stop local tracks
      stopLocalTracks();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run cleanup on unmount, not when twilioRoom changes

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content video-conference-content">
              <div className="loading-message">Loading video call...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !appointment || !callRequest) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content video-conference-content">
              <div className="error-message">
                {error || 'Video call not available for this appointment'}
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  const patientName = appointment.patient_name || 'Patient';
  const patientInitials = patientName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  return (
    <div className="dashboard video-conference-dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        <div className="dashboard-container video-conference-container">
          <main className="video-conference-main">
            {/* Video Call Area */}
            <div className="video-call-area">
              {/* Main Video (Patient) */}
              <div className="main-video-container">
                {/* Always render video element so ref is available - must be visible for Twilio to work */}
                  <video
                    ref={remoteVideoRef}
                    autoPlay
                    playsInline
                    className="remote-video-element"
                  style={{ 
                    display: 'block',
                    opacity: (remoteVideoTrack && hasRemoteParticipant) ? 1 : 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }}
                />
                {/* Hidden audio element for remote audio track */}
                <audio
                  ref={remoteAudioRef}
                  autoPlay
                  playsInline
                  style={{ display: 'none' }}
                />
                {(!remoteVideoTrack || !hasRemoteParticipant) && (
                  <div className="video-placeholder">
                    <div className="video-placeholder-avatar">{patientInitials}</div>
                    <div className="video-placeholder-name">{patientName}</div>
                    <div className="video-placeholder-status">
                      {connectionStatus === 'connecting' ? 'Connecting...' : 'Waiting for patient to join'}
                  </div>
                  </div>
                )}
              </div>

              {/* Local Video Preview (Doctor) */}
              <div className="local-video-preview">
                {/* Always render video element so ref is available - must be visible for Twilio to work */}
                  <video
                  ref={localVideoCallbackRef}
                    autoPlay
                    playsInline
                    muted
                    className="local-video-element"
                  style={{ 
                    display: 'block',
                    opacity: (localVideoTrack && isCameraEnabled) ? 1 : (localVideoTrack ? 0.3 : 0),
                    pointerEvents: 'none',
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  />
                {!localVideoTrack && (
                  <div className="local-video-placeholder">
                    <div className="local-video-avatar">DR</div>
                  </div>
                )}
                {localVideoTrack && !isCameraEnabled && (
                  <div className="local-video-placeholder" style={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    pointerEvents: 'none',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                  }}>
                    <div className="local-video-avatar">DR</div>
                  </div>
                )}
              </div>

              {/* Call Info Overlay */}
              <div className="call-info-overlay">
                <div className="call-info">
                  <div className="call-patient-name">{patientName}</div>
                  {callDuration && <div className="call-duration">{callDuration}</div>}
                  {isRecording && (
                    <div className="recording-indicator">
                      <span className="recording-dot"></span>
                      REC
                    </div>
                  )}
                </div>
              </div>

              {/* Permission Error Banner */}
              {permissionError && (
                <div className="permission-error-banner">
                  <div className="permission-error-content">
                    <span className="permission-error-icon">⚠️</span>
                    <span className="permission-error-text">{permissionError}</span>
                    <button
                      className="permission-retry-button"
                      onClick={() => {
                        if (callRequest && appointment) {
                          // Prevent retry if appointment is completed
                          if (appointment.status === 'completed') {
                            alert('This appointment has been completed. Video calls are no longer available.');
                            return;
                          }
                          console.log('🔄 Retry button clicked');
                          hasConnectedRef.current = false;
                          setPermissionError(null);
                          setConnectionStatus('disconnected');
                          connectToTwilioRoom(callRequest.channel_name, callRequest.doctor_id);
                        }
                      }}
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}
              
              {/* Connection Timeout Warning */}
              {connectionStatus === 'connecting' && !permissionError && (
                <div className="permission-error-banner" style={{ backgroundColor: '#f59e0b' }}>
                  <div className="permission-error-content">
                    <span className="permission-error-icon">⏳</span>
                    <span className="permission-error-text">
                      Connection is taking longer than expected. Please check your camera/microphone permissions and try again.
                    </span>
                    <button
                      className="permission-retry-button"
                      onClick={() => {
                        if (callRequest && appointment) {
                          // Prevent retry if appointment is completed
                          if (appointment.status === 'completed') {
                            alert('This appointment has been completed. Video calls are no longer available.');
                            return;
                          }
                          console.log('🔄 Manual retry from timeout warning');
                          hasConnectedRef.current = false;
                          setConnectionStatus('disconnected');
                          connectToTwilioRoom(callRequest.channel_name, callRequest.doctor_id);
                        }
                      }}
                    >
                      Retry Connection
                    </button>
                  </div>
                </div>
              )}

              {/* Manual Connect Button - Show if not connected and no error */}
              {callRequest && !twilioRoom && !permissionError && connectionStatus !== 'connected' && (
                <div className="manual-connect-banner">
                  <div className="manual-connect-content">
                    <span className="manual-connect-text">
                      {connectionStatus === 'connecting' ? 'Connecting...' : 'Click to start video call'}
                    </span>
                    <button
                      className="manual-connect-button"
                      onClick={() => {
                        if (callRequest && appointment && !hasConnectedRef.current) {
                          // Prevent connection if appointment is completed
                          if (appointment.status === 'completed') {
                            alert('This appointment has been completed. Video calls are no longer available.');
                            return;
                          }
                          console.log('🔄 Manual connect triggered');
                          hasConnectedRef.current = false;
                          setConnectionStatus('connecting');
                          connectToTwilioRoom(callRequest.channel_name, callRequest.doctor_id);
                        }
                      }}
                      disabled={connectionStatus === 'connecting' || (appointment?.status === 'completed')}
                    >
                      {connectionStatus === 'connecting' ? 'Connecting...' : 'Connect'}
                    </button>
                  </div>
                </div>
              )}

              {/* Call Controls */}
              <div className="call-controls">
                    <button
                      className={`control-button ambient-listening ${isRecording ? 'recording' : ''} ${isProcessing ? 'processing' : ''}`}
                      onClick={isRecording ? handleStopRecording : handleStartRecording}
                      disabled={!twilioRoom || isProcessing || isCallEnded}
                      style={
                        isProcessing 
                          ? { backgroundColor: '#6b7280', color: '#ffffff', cursor: 'wait' }
                          : isRecording 
                          ? { backgroundColor: '#10b981', color: '#ffffff' }
                          : { backgroundColor: '#6b7280', color: '#ffffff' }
                      }
                    >
                      {isProcessing ? 'Processing...' : isRecording ? 'Stop Listening' : 'Ambient Listening'}
                    </button>

                <div className="primary-controls">
                  <button
                    className={`control-button ${isMuted ? 'muted' : ''}`}
                    onClick={handleMute}
                    title={isMuted ? 'Unmute' : 'Mute'}
                    disabled={isCallEnded}
                  >
                    {isMuted ? '🎤' : '🎤'}
                  </button>
                  {isCallEnded ? (
                    <button
                      className="control-button reconnect"
                      onClick={handleReconnect}
                      title="Reconnect"
                      disabled={connectionStatus === 'connecting'}
                    >
                      {connectionStatus === 'connecting' ? '🔄' : '🔄'}
                    </button>
                  ) : (
                  <button
                    className="control-button end-call"
                    onClick={handleEndCall}
                    title="End Call"
                  >
                    📞
                  </button>
                  )}
                  <button
                    className={`control-button ${!isCameraEnabled ? 'disabled' : ''}`}
                    onClick={handleToggleCamera}
                    title={isCameraEnabled ? 'Turn off camera' : 'Turn on camera'}
                    disabled={isCallEnded}
                  >
                    {isCameraEnabled ? '📹' : '📹'}
                  </button>
                </div>
              </div>
            </div>

            {/* Appointment Discussion Panel */}
            <div className="appointment-discussion">
              <div className="discussion-header">
                <h2 className="discussion-title">Appointment Discussion</h2>
                {appointment && appointment.status !== 'completed' && (
                  <button
                    className="mark-complete-button"
                    onClick={handleMarkAsComplete}
                    disabled={isMarkingComplete}
                    title="Mark Appointment as Complete"
                  >
                    {isMarkingComplete ? (
                      <>
                        <span className="spinner"></span>
                        <span>Marking...</span>
                      </>
                    ) : (
                      <>
                        <span>✓</span>
                        <span>Mark as Complete</span>
                      </>
                    )}
                  </button>
                )}
                {appointment && appointment.status === 'completed' && (
                  <div className="appointment-completed-badge">
                    <span>✓</span>
                    <span>Completed</span>
                  </div>
                )}
              </div>

              <div className="discussion-sections">
                  {/* Chat Section */}
                  <div className="chat-section">
                    <div className="section-header">
                      <h3 className="section-title">Chat</h3>
                      <div className="recipient-selector">
                        <button
                          className={`recipient-tab ${selectedRecipient === 'patient' ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedRecipient('patient');
                            setIsAgentProcessing(false); // Clear processing when switching recipients
                          }}
                          title="Chat with Patient"
                        >
                          Patient
                        </button>
                        <button
                          className={`recipient-tab ${selectedRecipient === 'ai_agent' ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedRecipient('ai_agent');
                            setIsAgentProcessing(false); // Clear processing when switching recipients
                          }}
                          title="Chat with AI Agent"
                        >
                          AI Agent
                        </button>
                      </div>
                    </div>
                    <div className="chat-messages-container">
                      {chatMessages.length > 0 ? (
                        <div className="chat-messages">
                          {chatMessages.map((message) => {
                            const isDoctorMessage = message.sender_type === 'doctor';
                            const isAIAgentMessage = message.sender_type === 'ai_agent';
                            const isPatientMessage = message.sender_type === 'user' || message.sender_type === 'patient';
                            const messageClass = isDoctorMessage ? 'sent' : isAIAgentMessage ? 'received ai-agent' : 'received';
                            
                            // Determine sender label (similar to iOS)
                            let senderLabel = '';
                            if (isDoctorMessage) {
                              if (message.recipient_type === 'ai_agent') {
                                senderLabel = 'Doctor → AI Agent';
                              } else if (message.recipient_type === 'patient' || message.recipient_type === 'user') {
                                senderLabel = 'Doctor → Patient';
                              } else {
                                senderLabel = 'Doctor';
                              }
                            } else if (isAIAgentMessage) {
                              senderLabel = 'AI Agent';
                            } else if (isPatientMessage) {
                              if (message.recipient_type === 'ai_agent') {
                                senderLabel = 'You → AI Agent';
                              } else if (message.recipient_type === 'doctor') {
                                senderLabel = 'You → Doctor';
                              } else {
                                senderLabel = 'You';
                              }
                            }
                            
                            return (
                            <div
                              key={message.id}
                                className={`chat-message ${messageClass}`}
                            >
                              {/* Sender/Recipient label above message */}
                              {senderLabel && (
                                <div className="chat-message-sender-label">
                                  {senderLabel}
                                </div>
                              )}
                                <div className="chat-message-content">
                                <div 
                                  dangerouslySetInnerHTML={{ __html: formatChatMessage(message.content) }}
                                />
                                </div>
                              {message.created_at && (
                                <div className="chat-message-time">
                                  {new Date(message.created_at).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </div>
                              )}
                            </div>
                            );
                          })}
                          {/* Show processing indicator when AI agent is processing */}
                          {isAgentProcessing && selectedRecipient === 'ai_agent' && (
                            <div className="chat-message received ai-agent processing-indicator">
                              <div className="chat-message-content">
                                <span className="processing-dots">
                                  <span>.</span>
                                  <span>.</span>
                                  <span>.</span>
                                </span>
                                <span className="processing-text">AI Agent is thinking</span>
                              </div>
                            </div>
                          )}
                          <div ref={chatMessagesEndRef} />
                        </div>
                      ) : (
                        <div className="no-chat-messages">
                          {isAgentProcessing && selectedRecipient === 'ai_agent' ? (
                            <div className="processing-indicator">
                              <span className="processing-dots">
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                              </span>
                              <span className="processing-text">AI Agent is thinking</span>
                            </div>
                          ) : (
                            'No messages yet'
                          )}
                        </div>
                      )}
                    </div>
                    <div className="chat-input-container">
                      <div className="chat-input-wrapper">
                      <input
                        type="text"
                        className="chat-input"
                          placeholder={`Type your message to ${selectedRecipient === 'patient' ? 'patient' : 'AI agent'}...`}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && newMessage.trim() && callRequest && !isAgentProcessing) {
                            handleSendMessage();
                          }
                        }}
                          disabled={isAgentProcessing && selectedRecipient === 'ai_agent'}
                      />
                        <div className="recipient-indicator">
                          {selectedRecipient === 'patient' ? '👤' : '🤖'}
                        </div>
                      </div>
                    <button
                        className="chat-send-button"
                        onClick={() => {
                          if (newMessage.trim() && callRequest && !(isAgentProcessing && selectedRecipient === 'ai_agent')) {
                            handleSendMessage();
                          }
                        }}
                        disabled={!newMessage.trim() || !callRequest || (isAgentProcessing && selectedRecipient === 'ai_agent')}
                    >
                        ➤
                    </button>
                    </div>
                  </div>

                  {/* Clinical Section */}
                  <div className="clinical-section">
                    <div className="section-header">
                      <h3 className="section-title">Clinical</h3>
                    </div>
                    <div className="clinical-content">
                      {/* Clinical Sub-tabs */}
                      <div className="clinical-sub-tabs">
                        <button
                          className={`clinical-sub-tab ${activeChatSubTab === 'summary' ? 'active' : ''}`}
                          onClick={() => setActiveChatSubTab('summary')}
                        >
                          Summary
                        </button>
                        <button
                          className={`clinical-sub-tab ${activeChatSubTab === 'prescription' ? 'active' : ''}`}
                          onClick={() => setActiveChatSubTab('prescription')}
                        >
                          Prescription
                        </button>
                        <button
                          className={`clinical-sub-tab ${activeChatSubTab === 'labTests' ? 'active' : ''}`}
                          onClick={() => setActiveChatSubTab('labTests')}
                        >
                          Lab Tests
                        </button>
                      </div>

                      {/* Summary Sub-tab */}
                      {activeChatSubTab === 'summary' && (
                        <div className="summary-content">
                          <div className="summary-header">
                            <h4 className="summary-title">Summary</h4>
                            {!isEditingSummary && (
                              <button
                                className="edit-summary-button"
                                onClick={() => setIsEditingSummary(true)}
                              >
                                ✏️ Edit
                              </button>
                            )}
                          </div>
                          {isEditingSummary ? (
                            <div className="summary-edit">
                              <textarea
                                className="summary-textarea"
                                value={editedSummary}
                                onChange={(e) => setEditedSummary(e.target.value)}
                                rows={10}
                              />
                              <div className="summary-edit-actions">
                                <button
                                  className="save-button"
                                  onClick={handleSaveSummary}
                                >
                                  Save
                                </button>
                                <button
                                  className="cancel-button"
                                  onClick={() => {
                                    setIsEditingSummary(false);
                                    setEditedSummary(clinicalData?.summary || '');
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="summary-text">
                              {clinicalData?.summary || 'No summary available'}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Prescription Sub-tab */}
                      {activeChatSubTab === 'prescription' && (
                        <div className="prescription-content">
                          <div className="prescription-header-section">
                          <h4 className="section-subtitle">Prescriptions</h4>
                            {!isAddingPrescription && (
                              <button
                                className="add-button"
                                onClick={handleAddPrescription}
                              >
                                + Add Prescription
                              </button>
                            )}
                          </div>
                          {isAddingPrescription && (
                            <div className="edit-form" style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                              <input
                                type="text"
                                className="edit-input"
                                placeholder="Medication Name *"
                                value={editPrescriptionForm.medication_name}
                                onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, medication_name: e.target.value })}
                              />
                              <input
                                type="text"
                                className="edit-input"
                                placeholder="Dosage (e.g., 250 mg)"
                                value={editPrescriptionForm.dosage}
                                onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, dosage: e.target.value })}
                              />
                              <input
                                type="text"
                                className="edit-input"
                                placeholder="Frequency (e.g., twice a day)"
                                value={editPrescriptionForm.frequency}
                                onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, frequency: e.target.value })}
                              />
                              <input
                                type="text"
                                className="edit-input"
                                placeholder="Duration (e.g., 7 days)"
                                value={editPrescriptionForm.duration}
                                onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, duration: e.target.value })}
                              />
                              <textarea
                                className="edit-textarea"
                                placeholder="Instructions"
                                value={editPrescriptionForm.instructions}
                                onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, instructions: e.target.value })}
                                rows={3}
                              />
                              <div className="edit-actions">
                                <button
                                  className="save-button"
                                  onClick={handleCreatePrescription}
                                >
                                  Create
                                </button>
                                <button
                                  className="cancel-button"
                                  onClick={() => setIsAddingPrescription(false)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                          {clinicalData?.prescriptions && clinicalData.prescriptions.length > 0 ? (
                            <div className="prescriptions-list">
                              {clinicalData.prescriptions.map((prescription, index) => (
                                <div key={prescription.id || index} className="prescription-item">
                                  {editingPrescriptionId === prescription.id ? (
                                    <div className="edit-form">
                                      <input
                                        type="text"
                                        className="edit-input"
                                        placeholder="Medication Name"
                                        value={editPrescriptionForm.medication_name}
                                        onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, medication_name: e.target.value })}
                                      />
                                      <input
                                        type="text"
                                        className="edit-input"
                                        placeholder="Dosage (e.g., 250 mg)"
                                        value={editPrescriptionForm.dosage}
                                        onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, dosage: e.target.value })}
                                      />
                                      <input
                                        type="text"
                                        className="edit-input"
                                        placeholder="Frequency (e.g., twice a day)"
                                        value={editPrescriptionForm.frequency}
                                        onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, frequency: e.target.value })}
                                      />
                                      <input
                                        type="text"
                                        className="edit-input"
                                        placeholder="Duration (e.g., 7 days)"
                                        value={editPrescriptionForm.duration}
                                        onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, duration: e.target.value })}
                                      />
                                      <textarea
                                        className="edit-textarea"
                                        placeholder="Instructions"
                                        value={editPrescriptionForm.instructions}
                                        onChange={(e) => setEditPrescriptionForm({ ...editPrescriptionForm, instructions: e.target.value })}
                                        rows={3}
                                      />
                                      <div className="edit-actions">
                                        <button
                                          className="save-button"
                                          onClick={handleSavePrescription}
                                        >
                                          Save
                                        </button>
                                        <button
                                          className="cancel-button"
                                          onClick={() => setEditingPrescriptionId(null)}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="prescription-header">
                                  <div className="prescription-name">{prescription.medication_name}</div>
                                        <div className="prescription-actions">
                                          <button
                                            className="edit-button"
                                            onClick={() => handleEditPrescription(prescription)}
                                            title="Edit prescription"
                                          >
                                            ✏️
                                          </button>
                                          <button
                                            className="delete-button"
                                            onClick={() => handleDeletePrescription(prescription.id)}
                                            title="Delete prescription"
                                          >
                                            🗑️
                                          </button>
                                        </div>
                                      </div>
                                  {prescription.dosage && (
                                    <div className="prescription-detail">Dosage: {prescription.dosage}</div>
                                  )}
                                  {prescription.frequency && (
                                    <div className="prescription-detail">Frequency: {prescription.frequency}</div>
                                  )}
                                      {prescription.duration && (
                                        <div className="prescription-detail">Duration: {prescription.duration}</div>
                                      )}
                                  {prescription.instructions && (
                                    <div className="prescription-detail">Instructions: {prescription.instructions}</div>
                                      )}
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="no-content">No prescriptions</div>
                          )}
                        </div>
                      )}

                      {/* Lab Tests Sub-tab */}
                      {activeChatSubTab === 'labTests' && (
                        <div className="lab-tests-content">
                          <div className="lab-tests-header-section">
                          <h4 className="section-subtitle">Lab Tests</h4>
                            {!isAddingLabTest && (
                              <button
                                className="add-button"
                                onClick={handleAddLabTest}
                              >
                                + Add Lab Test
                              </button>
                            )}
                          </div>
                          {isAddingLabTest && (
                            <div className="edit-form" style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                              <input
                                type="text"
                                className="edit-input"
                                placeholder="Test Name *"
                                value={editLabTestForm.test_name}
                                onChange={(e) => setEditLabTestForm({ ...editLabTestForm, test_name: e.target.value })}
                              />
                              <input
                                type="text"
                                className="edit-input"
                                placeholder="Test Category"
                                value={editLabTestForm.test_category}
                                onChange={(e) => setEditLabTestForm({ ...editLabTestForm, test_category: e.target.value })}
                              />
                              <input
                                type="text"
                                className="edit-input"
                                placeholder="Priority (routine/urgent)"
                                value={editLabTestForm.priority}
                                onChange={(e) => setEditLabTestForm({ ...editLabTestForm, priority: e.target.value })}
                              />
                              <textarea
                                className="edit-textarea"
                                placeholder="Reason"
                                value={editLabTestForm.reason}
                                onChange={(e) => setEditLabTestForm({ ...editLabTestForm, reason: e.target.value })}
                                rows={2}
                              />
                              <textarea
                                className="edit-textarea"
                                placeholder="Instructions"
                                value={editLabTestForm.instructions}
                                onChange={(e) => setEditLabTestForm({ ...editLabTestForm, instructions: e.target.value })}
                                rows={2}
                              />
                              <textarea
                                className="edit-textarea"
                                placeholder="Notes"
                                value={editLabTestForm.notes}
                                onChange={(e) => setEditLabTestForm({ ...editLabTestForm, notes: e.target.value })}
                                rows={2}
                              />
                              <div className="edit-actions">
                                <button
                                  className="save-button"
                                  onClick={handleCreateLabTest}
                                >
                                  Create
                                </button>
                                <button
                                  className="cancel-button"
                                  onClick={() => setIsAddingLabTest(false)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                          {clinicalData?.lab_tests && clinicalData.lab_tests.length > 0 ? (
                            <div className="lab-tests-list">
                              {clinicalData.lab_tests.map((test, index) => (
                                <div key={test.id || index} className="lab-test-item">
                                  {editingLabTestId === test.id ? (
                                    <div className="edit-form">
                                      <input
                                        type="text"
                                        className="edit-input"
                                        placeholder="Test Name"
                                        value={editLabTestForm.test_name}
                                        onChange={(e) => setEditLabTestForm({ ...editLabTestForm, test_name: e.target.value })}
                                      />
                                      <input
                                        type="text"
                                        className="edit-input"
                                        placeholder="Test Category"
                                        value={editLabTestForm.test_category}
                                        onChange={(e) => setEditLabTestForm({ ...editLabTestForm, test_category: e.target.value })}
                                      />
                                      <input
                                        type="text"
                                        className="edit-input"
                                        placeholder="Priority (routine/urgent)"
                                        value={editLabTestForm.priority}
                                        onChange={(e) => setEditLabTestForm({ ...editLabTestForm, priority: e.target.value })}
                                      />
                                      <textarea
                                        className="edit-textarea"
                                        placeholder="Reason"
                                        value={editLabTestForm.reason}
                                        onChange={(e) => setEditLabTestForm({ ...editLabTestForm, reason: e.target.value })}
                                        rows={2}
                                      />
                                      <textarea
                                        className="edit-textarea"
                                        placeholder="Instructions"
                                        value={editLabTestForm.instructions}
                                        onChange={(e) => setEditLabTestForm({ ...editLabTestForm, instructions: e.target.value })}
                                        rows={2}
                                      />
                                      <textarea
                                        className="edit-textarea"
                                        placeholder="Notes"
                                        value={editLabTestForm.notes}
                                        onChange={(e) => setEditLabTestForm({ ...editLabTestForm, notes: e.target.value })}
                                        rows={2}
                                      />
                                      <div className="edit-actions">
                                        <button
                                          className="save-button"
                                          onClick={handleSaveLabTest}
                                        >
                                          Save
                                        </button>
                                        <button
                                          className="cancel-button"
                                          onClick={() => setEditingLabTestId(null)}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="lab-test-header">
                                  <div className="lab-test-name">{test.test_name}</div>
                                        <div className="lab-test-actions">
                                          <button
                                            className="edit-button"
                                            onClick={() => handleEditLabTest(test)}
                                            title="Edit lab test"
                                          >
                                            ✏️
                                          </button>
                                          <button
                                            className="delete-button"
                                            onClick={() => handleDeleteLabTest(test.id)}
                                            title="Delete lab test"
                                          >
                                            🗑️
                                          </button>
                                        </div>
                                      </div>
                                      {test.test_category && (
                                        <div className="lab-test-detail">Category: {test.test_category}</div>
                                      )}
                                      {test.priority && (
                                        <div className="lab-test-detail">Priority: {test.priority}</div>
                                      )}
                                  {test.reason && (
                                    <div className="lab-test-detail">Reason: {test.reason}</div>
                                  )}
                                  {test.instructions && (
                                    <div className="lab-test-detail">Instructions: {test.instructions}</div>
                                      )}
                                      {test.notes && (
                                        <div className="lab-test-detail">Notes: {test.notes}</div>
                                      )}
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="no-content">No lab tests</div>
                          )}
                        </div>
                      )}
                    </div>
                        </div>
                      </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default VideoConferencePage;

